import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Chip, useTheme, Avatar } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Footer from './Sections/Footer';
import ErrorIcon from '../assets/error.svg';

const FilteredAccountNotFoud = (props) => {
	const theme = useTheme();
	const isDark = props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const [showAllAccounts, setShowAllAccounts] = useState(false);
	
	// Hide stepper when this component is mounted
	useEffect(() => {
		global.showingFilteredAccountNotFound = true;
		window.dispatchEvent(new Event('stepperVisibilityChange'));
		
		return () => {
			global.showingFilteredAccountNotFound = false;
			window.dispatchEvent(new Event('stepperVisibilityChange'));
		};
	}, []);
	
	const retryCount = props.retryCount || 0;
	const maxRetries = props.maxRetries || 1;
	const isMaxRetriesReached = retryCount >= maxRetries;
	
	// Use missing accounts passed from parent (for partial matching)
	// or fall back to all targeted accounts from global (for complete no-match)
	const targetedAccounts = props.missingAccounts || global.redirectAccountRestrictions || [];
	
	// Get account numbers to display (max 2, then +X more)
	const displayAccounts = showAllAccounts ? targetedAccounts : targetedAccounts.slice(0, 2);
	const remainingCount = targetedAccounts.length - 2;
	
	// Format masked account number to show XX + lastDigits
	const formatMaskedAccountNumber = (maskedNumber) => {
		if (!maskedNumber) return '';
		// If already formatted with XX, return as is
		if (maskedNumber.startsWith('XX')) return maskedNumber;
		// Extract last 4 digits and format as XX1234
		const lastDigits = maskedNumber.slice(-4);
		return `XX${lastDigits}`;
	};
	
	const handleRetry = () => {
		// Re-trigger discovery without reloading page
		if (props.handleRetryDiscovery) {
			props.handleRetryDiscovery();
		}
	};
	
	const handleGoBack = async () => {
		props.history.push({
			pathname: `/consent-status`,
			state: {
				statusCode: 1,
			},
		});
	};
	
	// FIU Exit Icon component with dynamic color
	const FiuExitIconSvg = ({ color }) => (
		<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M6.57937 7.81333C6.72299 7.66958 6.79479 7.49569 6.79479 7.29167C6.79479 7.08764 6.72299 6.91375 6.57937 6.77C6.43562 6.62639 6.26174 6.55458 6.05771 6.55458C5.85368 6.55458 5.67979 6.62639 5.53604 6.77C5.39243 6.91375 5.32062 7.08764 5.32062 7.29167C5.32062 7.49569 5.39243 7.66958 5.53604 7.81333C5.67979 7.95694 5.85368 8.02875 6.05771 8.02875C6.26174 8.02875 6.43562 7.95694 6.57937 7.81333ZM2.72438 14.375V13.125L7.90875 12.4358C7.97278 12.4306 8.0275 12.4039 8.07292 12.3558C8.11833 12.3078 8.14104 12.2517 8.14104 12.1875V2.84458C8.14104 2.58278 8.05854 2.3525 7.89354 2.15375C7.7284 1.95514 7.52194 1.84194 7.27417 1.81417L2.98083 1.25V0L7.45188 0.589792C8.01493 0.674097 8.47889 0.926736 8.84375 1.34771C9.20861 1.76868 9.39104 2.26069 9.39104 2.82375V12.1619C9.39104 12.5326 9.26708 12.8595 9.01917 13.1427C8.77139 13.4258 8.46215 13.594 8.09146 13.6475L2.72438 14.375ZM2.72438 13.125H11.0577V1.50646C11.0577 1.4316 11.0337 1.37014 10.9856 1.32208C10.9376 1.27403 10.8761 1.25 10.8013 1.25H2.98083C2.90597 1.25 2.84451 1.27403 2.79646 1.32208C2.7484 1.37014 2.72438 1.4316 2.72438 1.50646V13.125ZM0.625 14.375C0.447639 14.375 0.299167 14.3151 0.179584 14.1954C0.0598613 14.0758 0 13.9274 0 13.75C0 13.5726 0.0598613 13.4242 0.179584 13.3046C0.299167 13.1849 0.447639 13.125 0.625 13.125H1.47438V1.50646C1.47438 1.0909 1.62153 0.735903 1.91583 0.441458C2.21028 0.147153 2.56528 0 2.98083 0H10.8013C11.2168 0 11.5718 0.147153 11.8663 0.441458C12.1606 0.735903 12.3077 1.0909 12.3077 1.50646V13.125H13.1571C13.3344 13.125 13.4829 13.1849 13.6025 13.3046C13.7222 13.4242 13.7821 13.5726 13.7821 13.75C13.7821 13.9274 13.7222 14.0758 13.6025 14.1954C13.4829 14.3151 13.3344 14.375 13.1571 14.375H0.625Z" fill={color} />
		</svg>
	);
	
	return (
		<>
		<Box 
			sx={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'space-between',
			padding: { xs: '1.5rem 1.5rem', sm: '1.5rem 1.5rem', md: '2.5rem 2rem' },
			minHeight: { xs: 'calc(100dvh - 70px)', sm: 'calc(100dvh - 70px)', md: '70vh' },
			backgroundColor: { 
				xs: isDark ? '#161616' : 'white',
				sm: isDark ? '#161616' : theme.palette.primary.light,
				md: isDark ? '#161616' : 'white'
			},
			border: { xs: 'none', sm: 'none', md: `1px solid ${isDark ? '#7A8185' : '#D9D9D9'}` },
			borderRadius: { xs: '0', sm: '0', md: '1rem' },
		gap: '1.5rem',
		width: '100%',
		maxWidth: { xs: '100%', sm: '100%', md: '50%' },
		mx: 'auto',
		mt: { xs: '8px', sm: '8px', md: 'calc(6.2rem + 8px)' },
		mb: { xs: '8px', sm: '8px', md: 'calc(6.2rem + 8px)' },
			}}
		>
			{/* Top Content */}
			<Box 
				sx={{ 
					display: 'flex', 
					flexDirection: 'column', 
					alignItems: 'center', 
					gap: '8px',
					flex: 1,
					justifyContent: 'center'
				}}
			>
			{/* Warning Icon */}
			<img
				src={ErrorIcon}
				alt="Error"
				style={{
					width: '40px',
					height: '40px',
					display: 'block',
				}}
			/>
				
			{/* Title */}
			<Typography 
				variant="h5" 
				sx={{ 
					fontWeight: 600,
					color: isDark ? '#FFFFFF' : theme.palette.text.primary,
					textAlign: 'center'
				}}
			>
				Unable to find accounts
			</Typography>
			
		{/* Description */}
		<Typography 
			variant="body1" 
			sx={{ 
				color: isDark ? '#B0B0B0' : theme.palette.text.secondary,
				textAlign: 'center',
				maxWidth: '400px'
			}}
		>
				Could not find any accounts with the selected banks. Please check the bank details & retry.
			</Typography>
			
			{/* Targeted Account Numbers */}
			{targetedAccounts.length > 0 && (
				<Box 
					sx={{ 
						display: 'flex', 
						gap: '0.5rem', 
						flexWrap: 'wrap',
						justifyContent: 'center'
					}}
				>
				{displayAccounts.map((account, index) => {
					const logoUrl = global.objectFormatFip[account.fipId]?.productIconUri;
					
					return (
						<Chip
							key={`${account.fipId}-${account.maskedAccNumber}-${index}`}
							avatar={
								logoUrl ? (
									<Avatar
										alt={global.objectFormatFip[account.fipId]?.fipName || account.fipId}
										src={logoUrl}
										variant="square"
										sx={{
											width: '12px !important',
											height: '12px !important',
											borderRadius: '2px',
											'& img': {
												width: '100%',
												height: '100%',
												objectFit: 'contain',
											}
										}}
									/>
								) : null
							}
							label={formatMaskedAccountNumber(account.maskedAccNumber)}
							sx={{
								backgroundColor: isDark ? '#3D3D3D' : '#F3F4F6',
								color: isDark ? '#B0B0B0' : theme.palette.text.secondary,
								fontWeight: 500,
								'& .MuiChip-avatar': {
									width: '12px',
									height: '12px',
									marginLeft: '8px',
									marginRight: '-4px',
								}
							}}
						/>
					);
				})}
					{remainingCount > 0 && !showAllAccounts && (
						<Chip
							label={`+${remainingCount} more account${remainingCount > 1 ? 's' : ''}`}
							onClick={() => setShowAllAccounts(true)}
							sx={{
								backgroundColor: isDark ? '#3D3D3D' : '#F3F4F6',
								color: isDark ? '#B0B0B0' : theme.palette.text.secondary,
								fontWeight: 500,
								cursor: 'pointer',
								'&:hover': {
									backgroundColor: isDark ? '#4D4D4D' : '#E5E7EB',
								}
							}}
						/>
					)}
					{showAllAccounts && targetedAccounts.length > 2 && (
						<Chip
							label="Show less"
							onClick={() => setShowAllAccounts(false)}
							sx={{
								backgroundColor: isDark ? '#3D3D3D' : '#F3F4F6',
								color: isDark ? '#B0B0B0' : theme.palette.text.secondary,
								fontWeight: 500,
								cursor: 'pointer',
								'&:hover': {
									backgroundColor: isDark ? '#4D4D4D' : '#E5E7EB',
								}
							}}
						/>
					)}
				</Box>
			)}
	</Box>

	{/* Action Buttons - At bottom of card */}
	<Box 
		sx={{ 
			display: 'flex', 
			gap: '1rem', 
			flexDirection: { 
				xs: isMaxRetriesReached ? 'column' : 'column-reverse', 
				sm: isMaxRetriesReached ? 'row-reverse' : 'row' 
			},
			justifyContent: 'center',
			alignItems: 'center',
			width: '100%',
			mt: 'auto',
		}}
	>
		{/* Go Back Button */}
		<Button
			variant={isMaxRetriesReached ? "contained" : "outlined"}
			startIcon={<FiuExitIconSvg color={isMaxRetriesReached ? (props.customDetails?.ctaTextColor || '#FFFFFF') : (isDark ? '#FFFFFF' : theme.palette.primary.main)} />}
			onClick={handleGoBack}
			disableRipple
			disableElevation
			sx={{
				textTransform: 'capitalize',
				fontSize: '.875rem',
				width: '240px',
				height: '48px',
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				...(isMaxRetriesReached ? {
					backgroundColor: props.customDetails?.primaryColor || '#3053D3',
					color: props.customDetails?.ctaTextColor || '#FFFFFF',
					boxShadow: 'none',
					'&:hover': {
						backgroundColor: props.customDetails?.primaryColor || '#3053D3',
						opacity: 0.9,
						boxShadow: 'none',
					},
				} : {
					backgroundColor: 'transparent',
					borderColor: isDark ? '#7A8185' : theme.palette.primary.main,
					color: isDark ? '#FFFFFF' : theme.palette.primary.main,
					'&:hover': {
						borderColor: isDark ? '#FFFFFF' : theme.palette.primary.dark,
						backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(48,83,211,0.05)',
					},
				}),
			}}
		>
			Go back to {global.entityName || 'Dhanaprayoga'}
		</Button>
		
		{/* Retry Button */}
		<Button
			variant={isMaxRetriesReached ? "outlined" : "contained"}
			startIcon={<RefreshIcon />}
			onClick={handleRetry}
			disabled={isMaxRetriesReached}
			disableRipple
			disableElevation
			sx={{
				textTransform: 'capitalize',
				fontSize: '.875rem',
				width: '240px',
				height: '48px',
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				...(isMaxRetriesReached ? {
					backgroundColor: 'transparent !important',
					borderColor: `${isDark ? '#4D4D4D' : '#D9D9D9'} !important`,
					color: `${isDark ? '#7A8185' : theme.palette.text.disabled} !important`,
					pointerEvents: 'none',
					cursor: 'not-allowed',
					'&.Mui-disabled': {
						borderColor: `${isDark ? '#4D4D4D' : '#D9D9D9'} !important`,
						color: `${isDark ? '#7A8185' : theme.palette.text.disabled} !important`,
					},
					'&:hover': {
						backgroundColor: 'transparent !important',
					},
				} : {
					boxShadow: 'none',
					backgroundColor: props.customDetails?.primaryColor || '#3053D3',
					color: props.customDetails?.ctaTextColor || '#FFFFFF',
					'&:hover': {
						backgroundColor: props.customDetails?.primaryColor || '#3053D3',
						opacity: 0.9,
						boxShadow: 'none',
					},
				}),
			}}
		>
			{`Retry ${retryCount > 0 ? `(${retryCount}/${maxRetries})` : ''}`}
		</Button>
	</Box>
</Box>
<Footer fixed={true} customDetails={props.customDetails} />
</>
	);
};

export default FilteredAccountNotFoud;
